import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Typography, InputBase, IconButton, Grid, Box, Button, Dialog,
  Tooltip,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { SearchOutlined, Add, Edit, Delete } from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddTask from './AddTaskList';
import { DeleteTaskMapping, GetAllCRMTags, GetAllCustomFields, GetProviderList, GetTaskMappings } from '../../Service/AxiosService';

const TaskList = () => {
  const [open, setOpen] = useState(false);
  const [taskMappings, setTaskMappings] = useState<any[]>([]);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [tagList, setTagList] = useState<any[]>([]);
  const [editRow, setEditRow] = useState<any | null>(null);
  const [deleteRow, setDeleteRow] = useState<any | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customFields, setCustomFields] = useState<any[]>([]);
  
  const columns: GridColDef[] = [
  { field: 'S_No', headerName: 'S.No', width: 20, headerAlign: 'center', align: 'center' },
  { field: 'GHLTagName', headerName: 'HL Tag Name', width: 150, headerAlign: 'left', align: 'left' },
  {
    field: 'TaskSubject',
    headerName: 'Task Subject',
    flex: 1,
    minWidth: 50,
    headerAlign: 'left',
    align: 'left',
    renderCell: (params) => {
      return (
        <Tooltip title={params.value} placement="top" arrow>
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', width: '100%' }}>
            {params.value}
          </span>
        </Tooltip>
      );
    },
  },
  {
    field: 'TaskDetails',
    headerName: 'Task Details',
    flex: 1,
    minWidth: 50,
    headerAlign: 'left',
    align: 'left',
    renderCell: (params) => (
      <Tooltip title={params.value} placement="top" arrow>
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', width: '100%' }}>
          {params.value}
        </span>
      </Tooltip>
    ),
  },
  { field: 'CerboId', headerName: 'Provider', width: 80, headerAlign: 'left', align: 'left' },
  //{ field: 'AssignTo', headerName: 'Assign To', width: 140, headerAlign: 'center', align: 'center' },
  { field: 'Priority', headerName: 'Priority', width: 120, headerAlign: 'center', align: 'center',
    renderCell: (params) => {
            const isAdd = params.value === 'Normal';
            return (
              <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Box
                  sx={{
                    color: isAdd ? '#3D52A0' : '#ff7f07',
                    fontWeight: 600,
                  }}
                >
                  {params.value}
                </Box>
              </Box>
            );
          },
   },
  { field: 'IsAddCerbo', headerName: 'Task Conditions', width: 100, headerAlign: 'center', align: 'center',
     renderCell: (params) => {
            const isAdd = params.value === 'Add';
            return (
              <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Box
                  sx={{
                    height: 24,
                    px: 1.5,
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 600,
                    display: 'flex',              // 👈 change here
                    alignItems: 'center',         // 👈 vertical center
                    justifyContent: 'center',     // 👈 horizontal center
                    color: isAdd ? '#15803d' : '#b91c1c',
                    backgroundColor: isAdd ? '#dcfce7' : '#fee2e2',
                  }}
                >
                  {params.value}
                </Box>
              </Box>
            );
          },
   },
  { field: 'HasValue', headerName: 'Enable Custom Field', width: 120, headerAlign: 'center', align: 'center',
    renderCell: (params) => {
            const isAdd = params.value === 'Enabled';
            return (
              <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Box
                  sx={{
                    color: isAdd ? '#0191d3' : '#6c757d',
                    fontWeight: isAdd ? '600' : '500',
                  }}
                >
                  {params.value}
                </Box>
              </Box>
            );
          },
   },
   {field: 'DueDate', headerName: 'Due Date', width: 100, headerAlign: 'center', align: 'center' },
  { field: 'customFieldId', headerName: 'Custom Field', width: 150, headerAlign: 'left', align: 'left' },
  { field: 'customFieldValue', headerName: 'Custom Field Value', width: 150, headerAlign: 'left', align: 'left' },
  {
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        filterable: false,
        disableReorder: true,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <IconButton
              aria-label="edit"
              color="primary"
              size="small"
              onClick={() => handleEdit(params.row)}
            >
              <Edit sx={{fontSize: 15}} />
            </IconButton>
            <IconButton
              aria-label="delete"
              color="error"
              size="small"
              onClick={() => handleDelete(params.row)}
            >
              <Delete sx={{fontSize: 15}} />
            </IconButton>
          </Box>
        ),
      },
];
  
  const dataGridWrapperRef = useRef<HTMLDivElement>(null);
  // Dynamic page size calculation
  const ROW_HEIGHT = 40;
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const calculatePageSize = (gridEl: HTMLDivElement) => {
    const header = gridEl.querySelector('.MuiDataGrid-columnHeaders');
    const footer = gridEl.querySelector('.MuiDataGrid-footerContainer');

    const headerHeight = header?.getBoundingClientRect().height ?? 0;
    const footerHeight = footer?.getBoundingClientRect().height ?? 0;
    const gridHeight = gridEl.getBoundingClientRect().height;

    const availableHeight = gridHeight - headerHeight - footerHeight;
    const rowsThatFit = Math.floor(availableHeight / ROW_HEIGHT);

    return Math.max(5, rowsThatFit);
  };

  const handleEdit = (row: any) => {
      setEditRow(row);
      setOpen(true);
    };
  
    const handleDelete = (row: any) => {
      setDeleteRow(row);
      setDeleteDialogOpen(true);
    };
  
    const handleConfirmDelete = async () => {
      if (deleteRow?.id) {
        await DeleteTaskMapping(deleteRow.id);
        await TaskMapping();
      }
      setDeleteDialogOpen(false);
      setDeleteRow(null);
    };

    const handleOnClose = async () => {
      setOpen(false);
      setEditRow(null);
      await TaskMapping();
    }
  

  useEffect(() => {
    document.title = `${process.env.REACT_APP_CLINIC_NAME || 'Clinic'} - Tasks`;

    if (!dataGridWrapperRef.current) return;
    const observer = new ResizeObserver(() => {
      const newPageSize = calculatePageSize(dataGridWrapperRef.current!);
      setPaginationModel(prev => {
        if (prev.pageSize === newPageSize) return prev;
        return { ...prev, pageSize: newPageSize, page: 0 };
      });
    });
    observer.observe(dataGridWrapperRef.current);
    return () => observer.disconnect();
  }, []);
  const pageSizeOptions = useMemo(() => [5, 10, 20, 50, paginationModel.pageSize].filter((v, i, arr) => arr.indexOf(v) === i).sort((a, b) => a - b), [paginationModel.pageSize]);

  useEffect(() => {
    TaskMapping();
  }, []);

const TaskMapping = async () => {
      let providers: any[] = [];
      const providersList = await GetProviderList();
      // Normalize providers response to an array before mapping
      const rawProviders = providersList?.data ?? providersList;
      const providersData: any[] = Array.isArray(rawProviders)
        ? rawProviders
        : Array.isArray(rawProviders?.data)
        ? rawProviders.data
        : [];

      providers = providersData.map((provider: any) => ({
        id: provider.providerId,
        name: `${provider.firstName} ${provider.middleName ? provider.middleName + ' ' : ''}${provider.lastName}`,
      }));
      setDoctors(providers);
      // An empty list is normal (nothing configured yet) — only alert on a real fetch failure.
      if (providersList && providersList.success === false) {
        alert("Failed to fetch provider list: " + (providersList.error ?? 'Unknown error'));
      }

      let customFields: any[] = [];
      const customFieldsList = await GetAllCustomFields();
      // Normalize custom fields response to an array before mapping
      const rawCustoms = customFieldsList?.data ?? customFieldsList;
      const customsData: any[] = Array.isArray(rawCustoms)
        ? rawCustoms
        : Array.isArray(rawCustoms?.data)
        ? rawCustoms.data
        : [];

      customFields = customsData.map((field: any) => ({
        fieldId: field.fieldId,
        fieldName: field.fieldName,
      }));
      setCustomFields(customFields);
      if (customFieldsList && customFieldsList.success === false) {
        alert("Failed to fetch custom fields list: " + (customFieldsList.error ?? 'Unknown error'));
      }

      const cerboTagIds = await GetAllCRMTags();
      const rawCerboTags = cerboTagIds?.data ?? cerboTagIds;
      const cerboTagsData: any[] = Array.isArray(rawCerboTags)
        ? rawCerboTags
        : Array.isArray(rawCerboTags?.data)
        ? rawCerboTags.data
        : [];

      const formattedTags = cerboTagsData.length > 0
        ? cerboTagsData.map((tag: any) => ({ id: tag.tagId, name: tag.tagName }))
        : [];

      setTagList(formattedTags);

      const res = await GetTaskMappings();
      const rawRes = res?.data ?? res;
      const resData: any[] = Array.isArray(rawRes)
        ? rawRes
        : Array.isArray(rawRes?.data)
        ? rawRes.data
        : [];

      const formattedData = resData.map((item: any, index: number) => ({
        id: item.id,
        S_No: index + 1,
        GHLTagName: formattedTags.find((tag: any) => tag.id === item.campaignTagId)?.name || item.GHLTagName,
        TaskSubject: item.cerboTaskSubject,
        TaskDetails: item.taskDetails,
        CerboId: providers.find((provider: any) => provider.id.toString() === item.cerboDoctorId?.toString())?.name || item.CerboId,
        DueDate: item.dueDate ?? 'none',
        AssignTo: undefined,
        Priority: item.cerboPriority,
        IsAddCerbo: item.isAddCerbo ? 'Add' : 'Remove',
        customFieldId: customFields.find((field: any) => field.fieldId === item.customFieldId)?.fieldName || item.customFieldId,
        customFieldValue: item.customFieldValue || '',
        HasValue: item.hasValue ? 'Enabled' : 'Disabled',
      }));
      setTaskMappings(formattedData);
      // Empty = a new clinic with no task rules yet; show the empty table, don't pop an error.
      if (res && res.success === false) {
        alert("Failed to fetch task mappings: " + (res.error ?? 'Unknown error'));
      }
    };


  return (
    <Box sx={{ width: '100%', height: '100%', p: 0, m: 0, bgcolor: 'transparent', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flexShrink: 0, zIndex: 1 }}>
        <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 1, mt: 0.5 }}>
          <Grid>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 17,
              }}
              variant="subtitle1"
            >
              Tasks
            </Typography>
          </Grid>
          <Grid>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="form"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: 300,
                  bgcolor: '#fff',
                  height: 36,
                  border: '1px solid #dedede',
                  borderRadius: 2,
                }}
              >
                <InputBase
                  placeholder="Search tasks..."
                  inputProps={{ 'aria-label': 'search tasks' }}
                  sx={{
                    flex: 1,
                    fontSize: 15,
                    bgcolor: 'transparent',
                    px: 2,
                    py: 0.5,
                    height: 32,
                  }}
                />
                <IconButton type="submit" sx={{ p: '4px', color: '#23244A', height: 32, width: 32 }} aria-label="search" size="small">
                  <SearchOutlined fontSize="small" />
                </IconButton>
              </Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add fontSize="small" />}
                sx={{
                  textTransform: 'none',
                  boxShadow: 'none',
                  bgcolor: '#3D52A0',
                  '&:hover': { bgcolor: '#31417f' },
                  fontSize: 14,
                  borderRadius: 2,
                  minWidth: 120,
                  py: 1,
                  px: 2,
                  height: 36,
                }}
                onClick={() => setOpen(true)}
              >
                New Task Rule
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box ref={dataGridWrapperRef}
        sx={{
          flex: 1,
          width: '100%',
          minHeight: 0,
          pb: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fff',
          borderRadius: 3,
          boxShadow: '0 2px 16px rgba(24,26,42,0.08)',
        }}
      >
        <DataGrid
          rows={taskMappings}
          columns={columns}
          pagination
          pageSizeOptions={pageSizeOptions}
          rowHeight={ROW_HEIGHT}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sx={{
            borderRadius: 3,
            bgcolor: '#fff',
            fontSize: 13,
            flex: 1,
            boxShadow: 'none',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: '#FAFAFC', 
              fontWeight: 700,
              fontSize: 13,
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
              }
            },
            '& .MuiDataGrid-cell': { fontSize: 12, py: 0, color: '#000' },
            '& .MuiDataGrid-row': { minHeight: 32, maxHeight: 32 },
            '& .MuiDataGrid-virtualScroller': { overflowX: 'auto !important' },
            '& .MuiTablePagination-root': { fontSize: 12, borderTop: '1px solid #e0e0e0' },
            '& .MuiDataGrid-footerContainer': {
              bgcolor: '#FAFAFC',
              borderBottomLeftRadius: 12,
              borderBottomRightRadius: 12,
            },
            '& .MuiDataGrid-selectedRowCount': { color: '#23244A' },
            '& .MuiDataGrid-cell[data-field="actions"]': {
                position: 'sticky',
                right: 0,
                backgroundColor: '#fff',
                zIndex: 3,
              },

              '& .MuiDataGrid-columnHeader[data-field="actions"]': {
                position: 'sticky',
                right: 0,
                backgroundColor: '#FAFAFC',
                zIndex: 4,
              },
          }}
        />
      </Box>
      <Dialog open={open} onClose={handleOnClose} maxWidth="sm" fullWidth>
        <AddTask
          open={open}
          onClose={handleOnClose}
          providerList={doctors}
          tagList={tagList}
          initialvalues={editRow}
          customFields={customFields}
        />
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: '#FAFAFC',
            boxShadow: '0 2px 16px rgba(24,26,42,0.10)',
            minWidth: 320,
          }
        }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ fontSize: 15, maxHeight: "80vh", overflowY: "auto" }}>
          Are you sure you want to delete this Task mapping?
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} color="secondary" variant="outlined" size="small"
            sx={{
              borderRadius: 2,
              border: 'none',
              background: '#dedede',
              color: '#000',
              textTransform: 'capitalize',
              py: 1,
              px: 2,
              '&:hover': { background: '#d6d6d6' },
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" size="small"
            sx={{
              borderRadius: 2,
              background: '#d32f2f',
              textTransform: 'capitalize',
              py: 1,
              px: 2,
              '&:hover': { background: '#b71c1c' }
            }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskList;